import {
  Dispatch,
  FC,
  KeyboardEvent,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import classes from './FilterSelect.module.css';
import { ReactComponent as ArrowDown } from '../../../../../icons/ArrowDown.svg';
import useOnClickOutside from '../../../../../hooks/useOnClickOutside';
import DropdownMenuItem from '../../../../CandidateHome/DropdownMenuItem';
import { toPascalCase } from '../../../../../utils/toPascalCase';

interface Props {
  title: string;
  options: { id: string; text: string }[];
  setCategory: Dispatch<SetStateAction<string>>;
  placeholder: string;
  hideNoneOpt?: boolean;
}

const FilterSelect: FC<Props> = ({
  title,
  options,
  placeholder,
  setCategory,
  hideNoneOpt,
}) => {
  const [t] = useTranslation('global');
  const [searchParams, setSearchParams] = useSearchParams();
  const [showContent, setShowContent] = useState<boolean>(false);
  const parentRef = useRef<HTMLDivElement>(null);
  const dropdownContentRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(parentRef, () => setShowContent(false));
  const noneOption = [{ id: 'None', text: t('forms.none') }];
  const filterOptions: { id: string; text: string }[] = hideNoneOpt
    ? options
    : noneOption.concat(options);

  const handleClick = () => {
    setShowContent((s) => !s);
  };

  const onClick = (option: { id: string; text: string }) => {
    const newParams = new URLSearchParams(searchParams);
    setCategory(option.id);
    if (option.id !== 'None') {
      newParams.set(toPascalCase(title), option.id);
      setSearchParams(newParams);
    } else {
      newParams.delete(toPascalCase(title));
      setSearchParams(newParams);
    }
    setShowContent(false);
  };

  const onKeyDown = (ev: KeyboardEvent<HTMLDivElement>) => {
    const keyPressed = ev.key.toLowerCase();
    const firstCountryWithKeyPressedLetter = options.find(({ text }) =>
      text.toLowerCase().startsWith(keyPressed)
    );

    if (dropdownContentRef.current && firstCountryWithKeyPressedLetter) {
      const matchedIndex = filterOptions.findIndex(
        ({ text }) => text === firstCountryWithKeyPressedLetter.text
      );
      const matchedDivOption = dropdownContentRef.current.querySelector(
        `div:nth-child(${matchedIndex + 1})`
      ) as HTMLElement | null;

      if (matchedDivOption) {
        dropdownContentRef.current.scrollTo({
          top: matchedDivOption.offsetTop,
          behavior: 'smooth',
        });
      }
    }
  };

  useEffect(() => {
    if (dropdownContentRef.current) {
      const matchedIndex = filterOptions.findIndex(
        ({ text }) => text === placeholder
      );
      const matchedDivOption = dropdownContentRef.current.querySelector(
        `div:nth-child(${matchedIndex + 1})`
      ) as HTMLElement | null;

      if (matchedDivOption) {
        dropdownContentRef.current.scrollTo({
          top: matchedDivOption.offsetTop,
        });
      }
    }
  }, [filterOptions, placeholder, showContent]);

  useEffect(() => {
    const optionSelect = searchParams.get(toPascalCase(title));
    if (optionSelect) {
      setCategory(optionSelect);
    }
  }, [title, searchParams, setCategory]);

  return (
    <div className={classes.FilterContainer} ref={parentRef}>
      <p className={classes.Title}>{title}</p>
      <div
        role="button"
        className={classes.Button}
        onClick={handleClick}
        onKeyDown={onKeyDown}
        tabIndex={0}
        data-testid="test-button"
      >
        <span className={classes.Placeholder}>
          {placeholder === t('forms.none') ? t('forms.select') : placeholder}
        </span>
        <ArrowDown />
      </div>
      {showContent && (
        <div
          className={`${classes.DropdownContent} max-h-96 overflow-auto w-full`}
          data-testid="dropdownContent"
          ref={dropdownContentRef}
        >
          {filterOptions.map((option) => (
            <DropdownMenuItem
              text={option.text}
              key={option.id}
              onClick={() => onClick(option)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

FilterSelect.defaultProps = {
  hideNoneOpt: false,
};

export default FilterSelect;
